import { useState, useEffect, useCallback } from "react";
import { makeApiRequest, apiRoutes } from "constants/apiRoutes";
import { useAuth } from "utilities/auth";
import { useEffectiveCompanyId } from "hooks/useEffectiveCompanyId";

/**
 * CheckoutChamp routing mirror (SOUND-826 Phase 1) — internal-only, read-only.
 *
 * Thin makeApiRequest wrappers (house pattern, same as useWorkload; not React
 * Query). The routing routes are requireRole("internal") server-side, so these
 * are only mounted behind <RequireRole role="internal">.
 *
 *   GET  /routing/clients                          → { data: [client] }
 *   GET  /routing/clients/:companyId/snapshot      → { data: snapshot }
 *   GET  /routing/clients/:companyId/snapshots     → { data: [metadata] }
 *   POST /routing/clients/:companyId/refresh       → { data: metadata, stored }
 *
 * SOUND-827 Phase 2 — change sets (contract: scratchpad sound-827-contract.md):
 *   GET  /routing/clients/:companyId/change-sets   → { data: [changeSet] }
 *   POST /routing/clients/:companyId/change-sets   → 201 { data, warnings }
 *   GET  /routing/change-sets/:id                  → { data, review }
 *   POST /routing/change-sets/:id/apply            → { data } | 409 { error, refusals }
 *   POST /routing/change-sets/:id/decline          → { data }
 *   POST /routing/change-sets/:id/rollback         → { data: <new pending set> }
 */
// `pick` chooses what to keep from the response body — `data` by default; the
// change-set detail also needs `review`, which sits beside it.
const pickData = (body) => body?.data ?? null;

function useGet(url, { enabled = true, pick = pickData } = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState(null);

  const auth = useAuth();
  const headerCompanyId = useEffectiveCompanyId();

  const fetchIt = useCallback(async () => {
    if (!enabled || !url || !headerCompanyId || !auth?.user) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await makeApiRequest("get", url, auth.user, headerCompanyId);
      setData(pick(res.data));
    } catch (err) {
      setData(null);
      setError(err);
    } finally {
      setLoading(false);
    }
    // `pick` is a module-level function at every call site, so it is stable.
  }, [enabled, url, headerCompanyId, auth?.user, pick]);

  useEffect(() => {
    fetchIt();
  }, [fetchIt]);

  return { data, loading, error, refetch: fetchIt };
}

export function useRoutingClients() {
  const { data, ...rest } = useGet(apiRoutes.routingClients);
  return { clients: Array.isArray(data) ? data : [], ...rest };
}

/** The latest snapshot when `snapshotId` is omitted. */
export function useRoutingSnapshot(companyId, snapshotId) {
  const { data, ...rest } = useGet(
    companyId ? apiRoutes.routingSnapshot(companyId, snapshotId) : null,
    { enabled: !!companyId }
  );
  return { snapshot: data, ...rest };
}

export function useRoutingSnapshots(companyId) {
  const { data, ...rest } = useGet(
    companyId ? apiRoutes.routingSnapshots(companyId) : null,
    { enabled: !!companyId }
  );
  return { snapshots: Array.isArray(data) ? data : [], ...rest };
}

/**
 * Take a snapshot now. Resolves to `{ data, stored }`; rejects with the axios
 * error (429 inside 60s of the last refresh — see refreshErrorMessage).
 */
export function useRoutingRefresh() {
  const auth = useAuth();
  const headerCompanyId = useEffectiveCompanyId();
  const [refreshing, setRefreshing] = useState(false);

  const refresh = useCallback(
    async (companyId) => {
      setRefreshing(true);
      try {
        const res = await makeApiRequest(
          "post",
          apiRoutes.routingRefresh(companyId),
          auth.user,
          headerCompanyId,
          {}
        );
        return { data: res.data?.data ?? null, stored: !!res.data?.stored };
      } finally {
        setRefreshing(false);
      }
    },
    [auth?.user, headerCompanyId]
  );

  return { refresh, refreshing };
}

// ---------------------------------------------------------------------------
// SOUND-827 — change sets
// ---------------------------------------------------------------------------

export function useRoutingChangeSets(companyId, status) {
  const { data, ...rest } = useGet(
    companyId ? apiRoutes.routingChangeSets(companyId, status) : null,
    { enabled: !!companyId }
  );
  return { changeSets: Array.isArray(data) ? data : [], ...rest };
}

const pickChangeSet = (body) =>
  body ? { changeSet: body.data ?? null, review: body.review ?? null } : null;

/** One change set plus the API's `review` (canApply, perChange locks, warnings). */
export function useRoutingChangeSet(changeSetId) {
  const { data, ...rest } = useGet(
    changeSetId ? apiRoutes.routingChangeSet(changeSetId) : null,
    { enabled: !!changeSetId, pick: pickChangeSet }
  );
  return { changeSet: data?.changeSet ?? null, review: data?.review ?? null, ...rest };
}

/**
 * The change-set writes. Each resolves to the response BODY and rejects with
 * the axios error (callers classify it via utilities/routingChanges). `busy`
 * names the action in flight so the page can disable everything at once — a
 * double-clicked GO must never send two applies.
 */
export function useRoutingChangeSetActions() {
  const auth = useAuth();
  const headerCompanyId = useEffectiveCompanyId();
  const [busy, setBusy] = useState(null);

  const post = useCallback(
    async (name, url, body) => {
      setBusy(name);
      try {
        const res = await makeApiRequest("post", url, auth.user, headerCompanyId, body || {});
        return res.data || {};
      } finally {
        setBusy(null);
      }
    },
    [auth?.user, headerCompanyId]
  );

  const create = useCallback(
    (companyId, body) => post("create", apiRoutes.routingChangeSets(companyId), body),
    [post]
  );
  const apply = useCallback(
    (changeSetId, approvedChangeIds) =>
      post("apply", apiRoutes.routingChangeSetApply(changeSetId), { approvedChangeIds }),
    [post]
  );
  const decline = useCallback(
    (changeSetId, note) =>
      post("decline", apiRoutes.routingChangeSetDecline(changeSetId), note ? { note } : {}),
    [post]
  );
  const rollback = useCallback(
    (changeSetId) => post("rollback", apiRoutes.routingChangeSetRollback(changeSetId), {}),
    [post]
  );

  return { create, apply, decline, rollback, busy };
}
